import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import MaturitySegmentDisplay from '@/components/maturityAssesment/MaturitySegmentDisplay';
import { getBandColor, getBandDescription } from "@/utils/maturityBanding";
import { maturityQuestions, getSelectedOption } from '../maturityOnlyResults/utils';
import type { MaturityResults } from "@shared/interfaces/maturityResultsInterface";
import type { MaturitySegment } from "@shared/interfaces/maturitySegment.interface";
import type { RecommendedAction } from "@shared/interfaces/recommendedAction.interface";

const PAGE_WIDTH = 794;
const PAGE_HEIGHT = 1123;

const pageStyle: React.CSSProperties = {
  width: `${PAGE_WIDTH}px`,
  minHeight: `${PAGE_HEIGHT}px`,
  padding: '48px',
  backgroundColor: '#0D1224',
  color: '#E2E8F0',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
};

type Props = {
  maturityResults: MaturityResults;
  clientName: string;
  projectName?: string | null;
  exportDate: string;
  operationalAnswers: number[];
  strategicAnswers: number[];
  maturitySegmentData?: MaturitySegment | null;
  recommendedActionsData?: RecommendedAction[] | null;
  includeCover?: boolean;
  includeExecutiveSummary?: boolean;
  includeMaturityMatrix?: boolean;
  includeStrategicAnalysis?: boolean;
  includeStrategicGuidance?: boolean;
  includeAppendix?: boolean;
};

const formatPercent = (value: number, total: number) => `${((value / total) * 100).toFixed(1)}%`;

export function MaturityReportDocument({
  maturityResults,
  clientName,
  projectName,
  exportDate,
  operationalAnswers,
  strategicAnswers,
  maturitySegmentData,
  recommendedActionsData,
  includeCover = true,
  includeExecutiveSummary = true,
  includeMaturityMatrix = true,
  includeStrategicAnalysis = true,
  includeStrategicGuidance = true,
  includeAppendix = true,
}: Props) {
  const topRecommendedActions = useMemo(() => {
    if (!recommendedActionsData || recommendedActionsData.length === 0) return [];

    if (maturitySegmentData?.highlighted_theme_ids?.length) {
      const highlighted = new Set(maturitySegmentData.highlighted_theme_ids);
      return [...recommendedActionsData]
        .sort((a, b) => {
          const aHighlighted = a.theme_id ? highlighted.has(a.theme_id) : false;
          const bHighlighted = b.theme_id ? highlighted.has(b.theme_id) : false;

          if (aHighlighted && !bHighlighted) return -1;
          if (!aHighlighted && bHighlighted) return 1;

          return (b.priority_score || 0) - (a.priority_score || 0);
        })
        .slice(0, 4);
    }

    return recommendedActionsData.slice(0, 4);
  }, [recommendedActionsData, maturitySegmentData]);

  return (
    <div style={{ width: `${PAGE_WIDTH}px` }} className="flex flex-col gap-8" data-report-root>
      {includeCover && (
        <section data-report-page style={pageStyle} className="report-page">
          <header className="flex items-center justify-between">
            <img
              src="/Redwood_Logo.png"
              alt="Redwood"
              width={180}
              height={48}
              style={{ objectFit: 'contain' }}
            />
            <div className="text-sm text-slate-500">R2R Maturity Assessment Report</div>
          </header>

          <div className="flex flex-1 flex-col items-center justify-center text-center gap-10">
            <div className="space-y-4">
              <h1 className="text-4xl font-bold text-white">R2R Maturity Assessment</h1>
              <h2 className="text-2xl text-slate-300">Results Report</h2>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-white">{clientName}</h3>
              <Badge variant="outline" className="text-base px-4 py-2 border-slate-500 text-slate-100">
                {maturityResults.operationalBand} Operations • {maturityResults.strategicBand} Strategic
              </Badge>
              <p className="text-slate-300">Generated: {exportDate}</p>
              {projectName && <p className="text-sm text-slate-400">Project: {projectName}</p>}
            </div>
          </div>
        </section>
      )}

      {includeExecutiveSummary && (
        <section data-report-page style={pageStyle} className="report-page">
          <h1 className="text-3xl font-bold text-slate-800">Executive Summary</h1>

          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Operational Maturity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {maturityResults.operationalScore}/30
                </div>
                <div className="text-sm text-slate-600 mb-2">
                  {formatPercent(maturityResults.operationalScore, 30)}
                </div>
                <Badge style={{ backgroundColor: getBandColor(maturityResults.operationalBand) }}>
                  {maturityResults.operationalBand}
                </Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Strategic Maturity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-emerald-600 mb-2">
                  {maturityResults.strategicScore}/25
                </div>
                <div className="text-sm text-slate-600 mb-2">
                  {formatPercent(maturityResults.strategicScore, 25)}
                </div>
                <Badge style={{ backgroundColor: getBandColor(maturityResults.strategicBand) }}>
                  {maturityResults.strategicBand}
                </Badge>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Strategic Position</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed">
                This organization demonstrates {maturityResults.operationalBand.toLowerCase()} operational capabilities
                combined with {maturityResults.strategicBand.toLowerCase()} strategic alignment.
                {' '}
                {getBandDescription(maturityResults.operationalBand)}
              </p>
            </CardContent>
          </Card>
        </section>
      )}

      {includeMaturityMatrix && (
        <section data-report-page style={pageStyle} className="report-page">
          <h1 className="text-3xl font-bold text-slate-800">Maturity Position Matrix</h1>

          <div className="rounded-xl border border-slate-200 p-4 bg-slate-50">
            <MaturitySegmentDisplay
              opScore={maturityResults.operationalScore}
              stratScore={maturityResults.strategicScore}
              printMode
            />
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm text-slate-600">
            <div>
              <h3 className="font-semibold text-slate-700 mb-2">Operational Bands</h3>
              <ul className="space-y-1">
                <li>Manual (0-10): Basic, manual processes</li>
                <li>Siloed (11-18): Isolated automation</li>
                <li>Managed (19-25): Coordinated processes</li>
                <li>Autonomous (26-30): Self-managing systems</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-slate-700 mb-2">Strategic Bands</h3>
              <ul className="space-y-1">
                <li>Reactive (0-8): Ad-hoc responses</li>
                <li>Controlled (9-15): Planned approaches</li>
                <li>Optimized (16-20): Performance-driven</li>
                <li>Predictive (21-25): Forward-looking</li>
              </ul>
            </div>
          </div>
        </section>
      )}

      {includeStrategicAnalysis && maturitySegmentData && (
        <section data-report-page style={pageStyle} className="report-page">
          <h1 className="text-3xl font-bold text-slate-800">Strategic Analysis</h1>

          <Card>
            <CardHeader>
              <CardTitle>{maturitySegmentData.segment_name}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed">{maturitySegmentData.summary_diagnosis}</p>
            </CardContent>
          </Card>

          {maturitySegmentData.symptoms && (
            <Card>
              <CardHeader>
                <CardTitle>Key Symptoms</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed">{maturitySegmentData.symptoms}</p>
              </CardContent>
            </Card>
          )}

          {maturitySegmentData.risks && (
            <Card>
              <CardHeader>
                <CardTitle>Implementation Blockers</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed">{maturitySegmentData.risks}</p>
              </CardContent>
            </Card>
          )}

          {maturitySegmentData.opportunities && (
            <Card>
              <CardHeader>
                <CardTitle>Growth Opportunities</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-700 leading-relaxed">{maturitySegmentData.opportunities}</p>
              </CardContent>
            </Card>
          )}
        </section>
      )}

      {includeStrategicGuidance && topRecommendedActions.length > 0 && (
        <section data-report-page style={pageStyle} className="report-page">
          <h1 className="text-3xl font-bold text-slate-800">Strategic Guidance</h1>

          <div className="space-y-6">
            {topRecommendedActions.map((action, index) => (
              <Card key={action.id ?? index} className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{index + 1}. {action.action_title}</span>
                    <div className="flex gap-2">
                      {action.action_type && <Badge variant="outline">{action.action_type}</Badge>}
                      {action.time_horizon && <Badge variant="outline">{action.time_horizon}</Badge>}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
                    {action.action_rationale && (
                      <p><strong>Rationale:</strong> {action.action_rationale}</p>
                    )}
                    {action.expected_outcome && (
                      <p><strong>Expected Outcome:</strong> {action.expected_outcome}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                      {action.effort_level && <span>Effort: {action.effort_level}</span>}
                      {action.owner_role && <span>Owner: {action.owner_role}</span>}
                      {action.priority_score && <span>Priority: {action.priority_score}</span>}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {includeAppendix && (
        <section data-report-page style={pageStyle} className="report-page">
          <h1 className="text-3xl font-bold text-slate-800">Appendix: Maturity Assessment Responses</h1>

          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-semibold text-slate-700 mb-4">Operational Maturity (Questions 1-6)</h2>
              <div className="space-y-4">
                {operationalAnswers.map((score, index) => (
                  <Card key={`operational-${index}`} className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Q{index + 1}: Score {score}/5</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm text-slate-700">
                        <p className="font-medium">{maturityQuestions[index]?.title || 'Question'}</p>
                        <p className="text-slate-500 italic">
                          {getSelectedOption(index, score)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-semibold text-slate-700 mb-4">Strategic Maturity (Questions 7-11)</h2>
              <div className="space-y-4">
                {strategicAnswers.map((score, index) => {
                  const questionIndex = index + operationalAnswers.length;
                  return (
                    <Card key={`strategic-${questionIndex}`} className="border-slate-200">
                      <CardHeader>
                        <CardTitle className="text-lg">Q{questionIndex + 1}: Score {score}/5</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm text-slate-700">
                          <p className="font-medium">{maturityQuestions[questionIndex]?.title || 'Question'}</p>
                          <p className="text-slate-500 italic">
                            {getSelectedOption(questionIndex, score)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default MaturityReportDocument;
